import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthCard } from '@/pages/auth/AuthCard';
import { PasswordInput } from '@/components/PasswordInput';
import { useLoginMutation } from '@/services/api';
import { useAppDispatch } from '@/store/hooks';
import { sessionEstablished } from '@/store/authSlice';
import { parseApiError } from '@/utils/apiError';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [login, { isLoading }] = useLoginMutation();

  const state = location.state as { from?: string; banner?: string } | null;
  const [banner, setBanner] = useState<string | null>(state?.banner ?? null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const canSubmit = email.trim() !== '' && password !== '' && !isLoading;

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBanner(null);
    try {
      const session = await login({ email: email.trim(), password }).unwrap();
      dispatch(sessionEstablished({ token: session.access_token }));
      navigate(state?.from ?? '/p', { replace: true });
    } catch (err) {
      setError(parseApiError(err).message);
    }
  }

  return (
    <AuthCard>
      {banner && (
        <div className="banner success" role="status">
          <span className="banner-icon">✓</span>
          <span>{banner}</span>
        </div>
      )}
      {error && (
        <div className="banner error" role="alert">
          <span className="banner-icon">!</span>
          <span>{error}</span>
        </div>
      )}
      <h1 className="title">Sign in</h1>
      <p className="subtitle">Welcome back to the AI Code Factory.</p>
      <form onSubmit={onSubmit} noValidate>
        <div className="field">
          <label className="field-label" htmlFor="login-email">
            Email
          </label>
          <input
            id="login-email"
            className="input"
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="login-password">
            Password
          </label>
          <PasswordInput
            allowAutofill
            id="login-password"
            placeholder="••••••••••••"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '-6px 0 4px' }}>
          <Link
            to="/forgot-password"
            style={{ borderBottom: '1px solid var(--text)', fontWeight: 500, color: 'var(--text)', fontSize: 13 }}
          >
            Forgot password?
          </Link>
        </div>
        <button className="btn-primary" type="submit" disabled={!canSubmit}>
          {isLoading ? 'Signing in…' : 'Sign in →'}
        </button>
      </form>
      <p className="auth-foot">
        New here? <Link to="/signup">Create an account</Link>
      </p>
    </AuthCard>
  );
}
